import { useState } from "react";
import CustomerCommsPanel from "@/components/CustomerCommsPanel";
import EmailComposeDialog from "@/components/EmailComposeDialog";
import { Mail, MailX, MessageSquare, PhoneOff, ShieldOff } from "lucide-react";

/**
 * CustomerCommsButtons — compact pair of icon buttons (SMS + Email) suitable
 * for list rows. Clicking stops propagation so the row click (navigate to
 * detail page) still fires only on the row body, not these icons.
 *
 * Pass the FULL customer shape (`{id, phone, email, sms_opted_out}`) so we
 * can disable with a tooltip reason without an extra network call.
 */
export default function CustomerCommsButtons({
  customer,
  jobId = null,
  invoiceId = null,
  estimateId = null,
  size = "sm",            // "sm" | "md"
  rowTestId = "",
}) {
  const [emailOpen, setEmailOpen] = useState(false);
  if (!customer) return null;

  const smsDisabled = !customer.phone || !!customer.sms_opted_out;
  const smsReason = customer.sms_opted_out
    ? "Opted out of SMS"
    : (!customer.phone ? "No phone on file" : "");
  const emailDisabled = !customer.email;
  const emailReason = emailDisabled ? "No email on file" : "";

  const btnCls = size === "sm"
    ? "w-7 h-7 inline-flex items-center justify-center rounded-md border border-zinc-200 hover:border-[#FF5A00] text-zinc-500 hover:text-[#FF5A00] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-zinc-200 disabled:hover:text-zinc-500"
    : "w-9 h-9 inline-flex items-center justify-center rounded-md border border-zinc-200 hover:border-[#FF5A00] text-zinc-500 hover:text-[#FF5A00] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-zinc-200 disabled:hover:text-zinc-500";

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  const SmsIcon = customer.sms_opted_out ? ShieldOff : (!customer.phone ? PhoneOff : MessageSquare);
  const EmailIcon = emailDisabled ? MailX : Mail;

  return (
    <div
      className="inline-flex items-center gap-1.5"
      onClick={stop}
      onKeyDown={stop}
      data-testid={rowTestId ? `${rowTestId}-comms-buttons` : "row-comms-buttons"}
    >
      <CustomerCommsPanel
        customerId={customer.id}
        customer={customer}
        jobId={jobId}
        invoiceId={invoiceId}
        estimateId={estimateId}
        renderTrigger={({ open, disabled, reason }) => (
          <button
            type="button"
            onClick={(e) => { stop(e); if (!disabled) open(); }}
            disabled={disabled}
            className={btnCls}
            title={disabled ? reason : "Text this customer"}
            data-testid={rowTestId ? `${rowTestId}-sms-btn` : "row-sms-btn"}
          >
            <SmsIcon className="w-3.5 h-3.5"/>
          </button>
        )}
      />
      <button
        type="button"
        onClick={(e) => { stop(e); if (!emailDisabled) setEmailOpen(true); }}
        disabled={emailDisabled}
        className={btnCls}
        title={emailDisabled ? emailReason : "Email this customer"}
        data-testid={rowTestId ? `${rowTestId}-email-btn` : "row-email-btn"}
      >
        <EmailIcon className="w-3.5 h-3.5"/>
      </button>
      <EmailComposeDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        customerId={customer.id}
        jobId={jobId}
        invoiceId={invoiceId}
        estimateId={estimateId}
        prefillTo={customer.email || ""}
      />
    </div>
  );
}
